import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import OutputIcon from '@mui/icons-material/Output'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import { 
  formatEventTimeColumn, 
  formatLocalDateTime,
  formatLocalTimeTooltip,
} from '@/utils/date'
import useEntryType from './useEntryType';
import { selectChannelIdToDirectionMap } from '@/features/channels/selectors'
import { useSelector } from 'react-redux'

const getBadgeColors = (count) => {
  if (count >= 4) return { bg: 'rgba(239,68,68,0.15)', color: '#b91c1c' }
  if (count >= 2) return { bg: 'rgba(251,191,36,0.2)', color: '#92400e' }
  return { bg: 'rgba(229,231,235,0.6)', color: '#4b5563' }
}

const EnteranceEventsFeedItem = ({ entry, channelName, onClick }) => {
  const { t } = useTranslation()
  const { 
    entryTypeTranslationMap, 
    entryTypeStatus 
  } = useEntryType()
  const count = entry.relatedEvents.length;
  const colors = getBadgeColors(count);
  const channelIdToDirectionMap = useSelector(selectChannelIdToDirectionMap);
  const direction = channelIdToDirectionMap[entry.channel_id];
  const directionColorNameMap = {
    in: 'warning.main',
    out: 'highlight.main',
  }
  const directionIconMap = {
    in: <ExitToAppIcon fontSize="small" color="inherit" />,
    out: <OutputIcon fontSize="small" color="inherit" sx={{ transform: 'rotate(180deg)' }} />,
  }
  const directionTranslationMap = useMemo(() => ({
    in: t('entrance-type_in'),
    out: t('entrance-type_out'),
  }), [t])
  
  const relatedEventsString = entry.relatedEvents.map((event) => 
    formatLocalTimeTooltip(event.created_at) + ' - ' 
    + (directionTranslationMap[event.direction] ? directionTranslationMap[event.direction] + ' - ' : '')
    + entryTypeTranslationMap[event.type]
  ).join('\n')

  const entryTypeColor = entryTypeStatus(entry.type) === 'good' ? 'success' : 'error';
  const entryCardBorderLeftColor = (direction !== 'in' )
    ? entryTypeColor + '.main'
    : directionColorNameMap[direction];

  return (
    <Paper
      elevation={2}
      onClick={onClick}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        borderLeftWidth: 4,
        borderLeftColor: entryCardBorderLeftColor,
        borderRadius: 2,
        transition: 'background 0.2s ease',
        '&:hover': { bgcolor: 'action.hover' },
        scrollSnapAlign: 'start',
        scrollMargin: '8px',
        animation: 'highlight 0.5s ease-in-out',
        '@keyframes highlight': {
          from: { 
            transform: 'scale(1.05)',
            backgroundColor: 'action.hover',
            opacity: 0.2,
          },
          to: { 
            transform: 'scale(1)',
            opacity: 1,
          },
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1
        }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            mb: 1
          }}>
          <Box
            sx={{
              height: 24,
              border: '1px solid',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {entry.plate_number}
              {/* {num} */}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{
            color: "text.disabled"
          }}>
            ·
          </Typography>
          {
            count > 0 && (
              <Tooltip 
                title={
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {`Количество доп. событий: ${count}\n${relatedEventsString}`}
                  </div>
                }
                arrow 
                placement="top"
              >
                <Box
                  sx={{
                    minWidth: 24,
                    height: 24,
                    borderRadius: '8px',
                    bgcolor: colors.bg,
                    color: colors.color,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {count}
                </Box>
              </Tooltip>
            )
          }
        </Stack>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              textAlign: 'end'
            }}>
            <VideocamOutlinedIcon 
              sx={{ 
                
                verticalAlign: 'middle',
                fontSize: 16,
                color: 'text.secondary',
                mr: 0.375,
                mb: '1px'
              }} 
            />
            {channelName || `${t('camera')} #${entry.channel_id}`}
          </Typography>  
        
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "space-between"
        }}>
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <Tooltip title={formatLocalDateTime(entry.created_at)} placement="top">
            <Chip
              size="small"
              label={formatEventTimeColumn(entry.created_at)}
              variant="outlined"
              sx={{
                fontWeight: 600,
                borderColor: 'text.primary',
              }}
            />
          </Tooltip>

          <Typography variant="caption" sx={{
            color: "text.disabled"
          }}>
            ·
          </Typography>
          <Chip
            size="small"
            label={entryTypeTranslationMap[entry.type] || entry.type}
            sx={{ 
              backgroundColor: entryTypeColor+'.main',
              color: '#FAFAFA'
            }}
          />
          {/* <Chip
            size="small"
            label={getVehicleTypeLabel(entry.vehicle_type, t)}
            variant="outlined"
          /> */}
        </Stack>
        <Chip
          size="small"
          icon={directionIconMap[direction]}
          label={directionTranslationMap[direction] || '-'}
          sx={{
            // color: directionColorNameMap[direction],
            // backgroundColor: directionColorNameMap[direction],
            justifyContent: 'flex-end',
            borderColor: directionColorNameMap[direction],
            color: 'text.primary',
          }}
          variant="outlined"
        />
      </Stack>
    </Paper>
  );
}

export default EnteranceEventsFeedItem;